import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_DRAFT_EXPIRATION_MS,
  DRAFT_FORMAT_VERSION,
  discardDraftEnvelope,
  draftStorageKey,
  readDraftEnvelope,
  saveDraftEnvelope,
  type DraftEnvelope,
} from '../utils/draftStorage';

type DraftPersistenceOptions<T> = {
  userId: string | null;
  screenType: string;
  entityId: string;
  value: T;
  initialValue: T;
  enabled?: boolean;
  debounceMs?: number;
  expirationMs?: number;
  version?: number;
};

export type DraftPersistenceResult<T> = {
  recoverableDraft: DraftEnvelope<T> | null;
  restoreDraft: () => T | null;
  discardDraft: () => void;
  clearDraft: () => void;
  draftSavedAt: string | null;
  isDirty: boolean;
};

function serialized(value: unknown): string {
  return JSON.stringify(value);
}

export function useDraftPersistence<T>({
  userId,
  screenType,
  entityId,
  value,
  initialValue,
  enabled = true,
  debounceMs = 500,
  expirationMs = DEFAULT_DRAFT_EXPIRATION_MS,
  version = DRAFT_FORMAT_VERSION,
}: DraftPersistenceOptions<T>): DraftPersistenceResult<T> {
  const key = useMemo(
    () => userId ? draftStorageKey(userId, screenType, entityId) : '',
    [entityId, screenType, userId],
  );
  const [recoverableDraft, setRecoverableDraft] = useState<DraftEnvelope<T> | null>(null);
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);
  const [recoveryResolved, setRecoveryResolved] = useState(false);
  const baselineRef = useRef(serialized(initialValue));
  const currentValueRef = useRef(value);
  currentValueRef.current = value;

  useEffect(() => {
    baselineRef.current = serialized(initialValue);
  }, [initialValue]);

  useEffect(() => {
    setDraftSavedAt(null);
    if (!enabled || !userId || !entityId) {
      setRecoverableDraft(null);
      setRecoveryResolved(true);
      return;
    }
    const existing = readDraftEnvelope<T>(
      window.localStorage,
      userId,
      screenType,
      entityId,
      Date.now(),
      expirationMs,
      version,
    );
    setRecoverableDraft(existing);
    setRecoveryResolved(!existing);
    if (existing && serialized(existing.data) === baselineRef.current) {
      discardDraftEnvelope(window.localStorage, userId, screenType, entityId);
      setRecoverableDraft(null);
      setRecoveryResolved(true);
    }
  }, [enabled, entityId, expirationMs, key, screenType, userId, version]);

  const isDirty = enabled && serialized(value) !== baselineRef.current;

  useEffect(() => {
    if (!enabled || !userId || !entityId || !recoveryResolved || !isDirty) return undefined;
    const timeout = window.setTimeout(() => {
      const updatedAt = new Date().toISOString();
      saveDraftEnvelope(window.localStorage, {
        version,
        updatedAt,
        userId,
        screenType,
        entityId,
        data: currentValueRef.current,
      });
      setDraftSavedAt(updatedAt);
    }, debounceMs);
    return () => window.clearTimeout(timeout);
  }, [debounceMs, enabled, entityId, isDirty, recoveryResolved, screenType, userId, value, version]);

  useEffect(() => {
    if (!isDirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  const restoreDraft = useCallback(() => {
    if (!recoverableDraft) return null;
    console.warn('Restoring local draft. Server data may have changed since the draft was created.', {
      key,
      updatedAt: recoverableDraft.updatedAt,
    });
    setRecoverableDraft(null);
    setRecoveryResolved(true);
    return recoverableDraft.data;
  }, [key, recoverableDraft]);

  const discardDraft = useCallback(() => {
    if (userId && entityId) discardDraftEnvelope(window.localStorage, userId, screenType, entityId);
    setRecoverableDraft(null);
    setRecoveryResolved(true);
    setDraftSavedAt(null);
  }, [entityId, screenType, userId]);

  const clearDraft = useCallback(() => {
    if (userId && entityId) discardDraftEnvelope(window.localStorage, userId, screenType, entityId);
    baselineRef.current = serialized(currentValueRef.current);
    setRecoverableDraft(null);
    setRecoveryResolved(true);
    setDraftSavedAt(null);
  }, [entityId, screenType, userId]);

  return {
    recoverableDraft,
    restoreDraft,
    discardDraft,
    clearDraft,
    draftSavedAt,
    isDirty,
  };
}
